// The page header inside the content column (DESIGN-SYSTEM §00 over §10.5; shell-ia §13): no panel, it sits on the window background —
// crumbs (the destination first, 12 px --label-2), the title 1 H1 (22/28 600, system face) with an optional sentence-case eyebrow, subtitle
// and status, the 28 px actions at the end (Export PDF and Ask about this screen when declared), then a 32 px row of underline tabs (13 px,
// 2 px navy underline, hairline below). Modules use ModuleHeader (WP-MODKIT) and desks DeskHeader (WP-DESK-HOST); kept NCP screens
// and ECONIC pages without a header of their own get this one through `usePageHeader`. WP-SHELL.
import type { ReactNode } from "react";
import { ChevronRight, FileDown, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { usePageHeader, useCurrentChrome, type PageChrome } from "../../shell/chrome";
import { useDock } from "../../shell/dock";
import { routeFor } from "../../app/routes";
import { destinationOf, PRIMARY, ADMIN } from "./destinations";
import { useClassification } from "./classification";

export { usePageHeader };
export type PageHeaderProps = PageChrome;

export function PageHeader(props: PageChrome & { as?: "h1" | "h2" }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dock = useDock();
  const { title, eyebrow, subtitle, status, actions, tabs, crumbs, ask, exportPdf, classification } = props;
  useClassification(classification ?? null, title);
  const dest = destinationOf(pathname);
  const destDef = [...PRIMARY, ...ADMIN].find((d) => d.id === dest);
  const trail = [...(destDef && destDef.href !== pathname ? [{ label: t(`assistant:econic.shell.nav.${destDef.label}`), href: destDef.href }] : []), ...(crumbs ?? [])];
  const H = props.as ?? "h1";
  return (
    <header className="es-phead" data-econic-page-header="">
      {trail.length ? (
        <nav className="crumbs" aria-label={t("assistant:econic.shell.header.crumbs")}>
          {trail.map((c, i) => (
            <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1.5">
              {i > 0 ? <ChevronRight aria-hidden /> : null}
              {c.href ? <Link to={c.href}>{c.label}</Link> : <span className="here">{c.label}</span>}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="row">
        <div className="tt">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          {title ? <H className="t-h1" tabIndex={-1} data-econic-h1="">{title}</H> : null}
          {subtitle ? <p className="sub">{subtitle}</p> : null}
          {status ? <div className="flex flex-wrap items-center gap-1.5">{status}</div> : null}
        </div>
        <div className="acts">
          {actions}
          {exportPdf ? (
            <button type="button" className="ec-btn secondary sm" onClick={() => void exportPdf()}>
              <FileDown aria-hidden />
              {t("assistant:econic.shell.header.exportPdf")}
            </button>
          ) : null}
          {ask && dock.available ? (
            <button type="button" className="ec-btn secondary sm" onClick={() => dock.ask(ask, { send: false })} data-ask-open="">
              <Sparkles aria-hidden />
              {t("assistant:econic.shell.header.ask")}
            </button>
          ) : null}
        </div>
      </div>
      {tabs?.length ? (
        <nav className="tabs" aria-label={title ?? ""}>
          {tabs.map((tab) => {
            const current = pathname === tab.href.split("?")[0];
            return (
              <Link key={tab.id} to={tab.href} aria-current={current ? "page" : undefined}>
                {tab.label}
                {typeof tab.count === "number" ? <span className="c">{tab.count}</span> : null}
              </Link>
            );
          })}
        </nav>
      ) : null}
    </header>
  );
}

export interface ScreenHeaderProps {
  /** The app-icon tile's glyph (lucide, drawn white at 21 px, stroke 1.6, on the 38 px --tile tile). */
  icon: ReactNode;
  title: ReactNode;
  /** The name in the other language, beside the title (15 px label-2); `otherLang` sets its language and face. */
  otherTitle?: ReactNode;
  otherLang?: "en" | "ar";
  /** The answer sentence (15/21/500, at most 800 px), hung on the title's text edge (50 px). */
  verdict?: ReactNode;
  /** A plain facts line after the title (Country360: "2nd non-oil trade partner · CEPA in force since 2022"). */
  facts?: ReactNode;
  /** The status line and/or meta line under the verdict (StatusLine, MetaLine). */
  meta?: ReactNode;
  /** The action group at the end: 28 px controls, the default action LAST. */
  actions?: ReactNode;
  /** Underline tabs (ScreenTabs) under the header. */
  tabs?: ReactNode;
}

/**
 * The module variant of the page header (APPLE-SPEC §6.2) for ECONIC screens with an app identity (Tasks, Trust, Admin, the Intelligence
 * hub): no panel; a 38 px app-icon tile · Title 1 · the other-language name · the actions at the end; under it, hung on the title's text
 * edge, the verdict (15/21/500) and the meta line; then the underline tabs. Phone: the tile is 32 px and nothing hangs.
 */
export function ScreenHeader({ icon, title, otherTitle, otherLang, verdict, facts, meta, actions, tabs }: ScreenHeaderProps) {
  return (
    <header className="es-shead" data-econic-page-header="">
      <div className="top">
        <span className="tile" aria-hidden>{icon}</span>
        <div className="ttl">
          <h1 className="t-title1" tabIndex={-1} data-econic-h1="">{title}</h1>
          {otherTitle ? <span className="alt" lang={otherLang} dir={otherLang === "ar" ? "rtl" : otherLang === "en" ? "ltr" : undefined}>{otherTitle}</span> : null}
          {facts ? <span className="facts">{facts}</span> : null}
        </div>
        {actions ? <div className="acts">{actions}</div> : null}
      </div>
      {verdict ? <p className="t-verdict verdict">{verdict}</p> : null}
      {meta ? <div className="meta">{meta}</div> : null}
      {tabs}
    </header>
  );
}

export interface ScreenTab {
  id: string;
  label: ReactNode;
  href: string;
  count?: number | null;
  current: boolean;
  onSelect?: () => void;
}

/** Underline tabs (APPLE-SPEC §6.2): 32 px on one hairline, 13/500 label-2, 22 px apart, the current one label 600 with a 2 px --tint
 *  indicator; counts 12 px label-2; an optional end link ("Table view"). */
export function ScreenTabs({ label, tabs, end }: { label: string; tabs: ScreenTab[]; end?: ReactNode }) {
  return (
    <nav className="ec-tabs es-stabs" aria-label={label}>
      {tabs.map((tab) => (
        <Link key={tab.id} to={tab.href} aria-current={tab.current ? "page" : undefined}
          onClick={tab.onSelect ? (e) => { e.preventDefault(); tab.onSelect!(); } : undefined}>
          {tab.label}
          {typeof tab.count === "number" ? <span className="n">{tab.count}</span> : null}
        </Link>
      ))}
      {end ? <span className="end">{end}</span> : null}
    </nav>
  );
}

/** The header of the screen in view, for kept screens that register one (`usePageChrome` / `usePageHeader`) on a `page` layout. */
export function ShellPageHeader() {
  const chrome = useCurrentChrome();
  const { pathname } = useLocation();
  const def = routeFor(pathname);
  const layout = chrome?.layout ?? def?.layout ?? "page";
  if (!chrome || layout !== "page" || !(chrome.title || chrome.crumbs?.length)) return null;
  return <PageHeader {...chrome} />;
}
